import { collectBrowserSizingParityMetrics } from "@/serverSizing/browserParityCalculation";
import {
  compareParityMetricValues,
  SIZING_PARITY_METRICS,
  type SizingParityMetric
} from "@/serverSizing/parityMetrics";
import type {
  RunSizingCalculationResponse,
  ScenarioContext,
  SizingMetric
} from "@/serverSizing/types";
import type { GoldenParityMismatch } from "@/serverSizing/goldenParity";
import { executeAction } from "./adminHttp";

export type RandomCompletedSizingCandidate = {
  id: string;
  title?: string | null;
  disposition?: string | null;
  pumping_type?: string | null;
  scenarios: Array<{
    id: string;
    title?: string | null;
  }>;
};

export type AdminParityResult = {
  fixtureId: string;
  sizingId: string;
  sizingTitle: string;
  scenarioId: string;
  scenarioTitle: string;
  status: RunSizingCalculationResponse["status"];
  traceId: string;
  traceVerified: boolean;
  engineVersion: string;
  engineHash: string;
  elapsedMs: number | null;
  browserMetrics: Record<string, SizingMetric>;
  serviceMetrics: Record<string, SizingMetric>;
  serviceWarnings: RunSizingCalculationResponse["warnings"];
  serviceHardFailures: RunSizingCalculationResponse["hardFailures"];
  mismatchCount: number;
  mismatches: GoldenParityMismatch[];
};

export type AdminParitySummary = {
  generatedAt: string;
  fixtureCount: number;
  mismatchCount: number;
  parityMatchedCount: number;
  statusCounts: Record<string, number>;
  byMetric: Record<string, number>;
  byLikelySource: Record<string, number>;
};

const shuffle = <T>(items: T[]): T[] => {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [
      shuffled[swapIndex],
      shuffled[index]
    ];
  }
  return shuffled;
};

export const discoverRandomCompletedSizings = async ({
  count,
  dispositions
}: {
  count: number;
  dispositions: string[];
}) => {
  const data = await executeAction<{
    sizings: RandomCompletedSizingCandidate[];
  }>("aiDosCompletedSizings", {
    limit: Math.max(count * 5, count),
    dispositions
  });
  return shuffle(data.sizings).slice(0, count);
};

const traceRetrievalMismatch = ({
  fixtureId,
  sizingId,
  scenarioId,
  traceId
}: {
  fixtureId: string;
  sizingId: string;
  scenarioId: string;
  traceId: string;
}): GoldenParityMismatch => ({
  fixtureId,
  sizingId,
  scenarioId,
  metricName: "traceRetrievable",
  frontendValue: null,
  serviceValue: null,
  unit: "trace",
  tolerance: 0,
  difference: null,
  traceId,
  severity: "error",
  likelySource: "trace_not_retrievable"
});

const compareMetric = ({
  fixtureId,
  sizingId,
  scenarioId,
  definition,
  frontendMetric,
  serviceMetric,
  traceId
}: {
  fixtureId: string;
  sizingId: string;
  scenarioId: string;
  definition: SizingParityMetric;
  frontendMetric?: SizingMetric;
  serviceMetric?: SizingMetric;
  traceId: string;
}): GoldenParityMismatch | null => {
  const comparison = compareParityMetricValues(
    definition,
    frontendMetric,
    serviceMetric
  );
  if (comparison.matched) return null;
  return {
    fixtureId,
    sizingId,
    scenarioId,
    metricName: definition.name,
    frontendValue: comparison.frontendValue,
    serviceValue: comparison.serviceValue,
    unit: comparison.unit,
    tolerance: definition.tolerance,
    difference: comparison.difference,
    traceId,
    severity: "error",
    likelySource: !frontendMetric
      ? "browser_metric_missing"
      : !serviceMetric
        ? "service_metric_missing"
        : frontendMetric.unit !== serviceMetric.unit
          ? "unit_mismatch"
          : comparison.difference === null
            ? "missing_value"
            : "outside_tolerance"
  };
};

export const runSizingParityForCandidate = async (
  candidate: RandomCompletedSizingCandidate,
  index: number
): Promise<AdminParityResult> => {
  const scenario = candidate.scenarios[0];
  if (!scenario) {
    throw new Error(`Sizing ${candidate.id} has no active scenarios.`);
  }
  const fixtureId = `admin-random-completed-${index + 1}`;
  const scenarioContext = await executeAction<ScenarioContext>(
    "aiDosSizingReportContext",
    { scenarioId: scenario.id }
  );
  const frontendMetrics = collectBrowserSizingParityMetrics(scenarioContext);
  const serviceResponse = await executeAction<RunSizingCalculationResponse>(
    "aiDosRunSizingCalculation",
    {
      scenarioId: scenario.id,
      calculationMode: "parity_fixture",
      debug: true
    }
  );
  // Trace retrieval failures are recorded as mismatches instead of aborting
  // the whole parity run: Phase 1 guarantees every trace ID is retrievable.
  let traceVerified = true;
  try {
    await executeAction("aiDosSizingTrace", {
      traceId: serviceResponse.traceId
    });
  } catch {
    traceVerified = false;
  }
  const mismatches = SIZING_PARITY_METRICS.map(definition =>
    compareMetric({
      fixtureId,
      sizingId: candidate.id,
      scenarioId: scenario.id,
      definition,
      frontendMetric: frontendMetrics[definition.name],
      serviceMetric: serviceResponse.metrics[definition.name],
      traceId: serviceResponse.traceId
    })
  ).filter((mismatch): mismatch is GoldenParityMismatch => mismatch !== null);
  if (!traceVerified) {
    mismatches.push(
      traceRetrievalMismatch({
        fixtureId,
        sizingId: candidate.id,
        scenarioId: scenario.id,
        traceId: serviceResponse.traceId
      })
    );
  }
  return {
    fixtureId,
    sizingId: candidate.id,
    sizingTitle: candidate.title || "Untitled sizing",
    scenarioId: scenario.id,
    scenarioTitle: scenario.title || "Untitled scenario",
    status: serviceResponse.status,
    traceId: serviceResponse.traceId,
    traceVerified,
    engineVersion: serviceResponse.engineVersion,
    engineHash: serviceResponse.engineHash,
    elapsedMs: serviceResponse.elapsedMs ?? null,
    browserMetrics: frontendMetrics,
    serviceMetrics: serviceResponse.metrics,
    serviceWarnings: serviceResponse.warnings,
    serviceHardFailures: serviceResponse.hardFailures,
    mismatchCount: mismatches.length,
    mismatches
  };
};

export const summarizeAdminParity = (
  results: AdminParityResult[]
): AdminParitySummary => {
  const mismatches = results.flatMap(result => result.mismatches);
  const countBy = (key: keyof GoldenParityMismatch) =>
    mismatches.reduce<Record<string, number>>((counts, mismatch) => {
      const value = String(mismatch[key]);
      counts[value] = (counts[value] || 0) + 1;
      return counts;
    }, {});
  return {
    generatedAt: new Date().toISOString(),
    fixtureCount: results.length,
    mismatchCount: mismatches.length,
    parityMatchedCount: results.filter(result => result.mismatchCount === 0)
      .length,
    statusCounts: results.reduce<Record<string, number>>((counts, result) => {
      counts[result.status] = (counts[result.status] || 0) + 1;
      return counts;
    }, {}),
    byMetric: countBy("metricName"),
    byLikelySource: countBy("likelySource")
  };
};
